import { Box, VStack, HStack, Text } from '@chakra-ui/react';
import { MdMenuBook as BookIcon, MdCollections as CollectionIcon } from 'react-icons/md';
import type { Recipe } from '@/types/api-models';
import type { RecipeCollection } from '@/types/api';
import type { PromptGroup } from '@/constants/chatPrompts';

interface PromptChipProps {
  label: string;
  onClick: () => void;
}

function PromptChip({ label, onClick }: PromptChipProps) {
  return (
    <HStack
      as="button"
      onClick={onClick}
      gap={3}
      bg="bg.surface"
      border="1.5px solid"
      borderColor="border.muted"
      borderRadius="12px"
      px={4}
      py={3}
      w="100%"
      align="center"
      textAlign="left"
      cursor="pointer"
      _hover={{ borderColor: 'accent.emphasis', bg: 'accent.subtle' }}
      transition="all 0.12s"
      boxShadow="0 1px 3px rgba(0,0,0,0.06)"
    >
      <Box
        w="6px"
        h="6px"
        borderRadius="full"
        bg="accent.secondary"
        flexShrink={0}
      />
      <Text fontFamily="body" fontSize="sm" color="text.primary" lineHeight="1.35" flex={1}>
        {label}
      </Text>
      <Text color="text.muted" fontSize="md" flexShrink={0}>›</Text>
    </HStack>
  );
}

interface ChatWelcomeCardProps {
  currentRecipe?: Recipe;
  currentCollection?: RecipeCollection;
  suggestedGroups: PromptGroup[];
  onSelectPrompt: (prompt: string) => void;
}

export default function ChatWelcomeCard({
  currentRecipe,
  currentCollection,
  suggestedGroups,
  onSelectPrompt,
}: ChatWelcomeCardProps) {
  const hasContext = !!(currentRecipe || currentCollection);
  const contextTitle = currentRecipe?.title ?? currentCollection?.title ?? null;
  const isCollection = !currentRecipe && !!currentCollection;

  const allPrompts = suggestedGroups.flatMap(g => g.prompts);

  return (
    <VStack gap={4} align="stretch" py={3} px={2}>

      {/* Identity card */}
      <Box
        bg="bg.surface"
        border="1.5px solid"
        borderColor="accent.secondaryBorder"
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="0 2px 16px rgba(0,0,0,0.08)"
      >
        {/* Context strip — only when recipe/collection is active */}
        {hasContext && contextTitle && (
          <HStack bg="accent.secondary" px={4} py={2.5} gap={2}>
            {isCollection
              ? <CollectionIcon size={12} color="rgba(255,255,255,0.75)" />
              : <BookIcon size={12} color="rgba(255,255,255,0.75)" />
            }
            <Text fontSize="9px" color="rgba(255,255,255,0.7)" letterSpacing="0.1em" textTransform="uppercase" flexShrink={0}>
              {isCollection ? 'Collection' : 'Recipe'}
            </Text>
            <Text fontSize="11px" color="white" fontWeight="600" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {contextTitle}
            </Text>
          </HStack>
        )}

        {/* Chef identity */}
        <HStack gap={4} px={5} pt={5} pb={5} align="center">
          <Box w="56px" h="56px" flexShrink={0}>
            <img src="/favicon.png" alt="Chef Assistant" width={56} height={56} style={{ display: 'block', objectFit: 'contain' }} />
          </Box>
          <VStack gap={1} align="start" flex={1}>
            <Text fontFamily="body" fontSize="sm" color="text.muted" lineHeight="1.5">
              {hasContext
                ? `I know this ${isCollection ? 'collection' : 'recipe'}. Ask about substitutions, scaling, or technique.`
                : 'Ask about cooking, substitutions, scaling, or get help with the app.'}
            </Text>
          </VStack>
        </HStack>
      </Box>

      {/* Suggested prompts */}
      {allPrompts.length > 0 && (
        <VStack gap={2} align="stretch">
          <Text fontSize="10px" color="text.muted" letterSpacing="0.1em" textTransform="uppercase" px={1} fontWeight="600">
            Try asking
          </Text>
          {allPrompts.map((prompt, i) => (
            <PromptChip key={i} label={prompt} onClick={() => onSelectPrompt(prompt)} />
          ))}
        </VStack>
      )}
    </VStack>
  );
}
